import { useMemo } from "react";
import { useQuery, useQueries } from "react-query";
import axios from "axios";
import { useUserProfiles } from "../../User/userProfileQueries";
import { useUserSuggestions } from "../../User/userProfileQueries";
import { fetchLeaves, fetchEvents, fetchHolidays, fetchGoogleCalendarEvents, fetchMicrosoftCalendarEvents, groupEventsByKey, fetchSyncedImportEvents } from "../services.js";
import {
    processLeaves,
    processInternalEvents,
    processBirthdays,
    processAnniversaries,
    processGoogleEvents,
    processMicrosoftEvents,
    processHolidays,
} from "./EventDataProcessor.js";
import { createQueryOptions } from "./queryErrorHandler.js";

/**
 * Custom hook for managing calendar event data
 * Uses useQueries for parallel fetching of independent queries
 */
export const useCalendarEvents = ({
    token,
    selectedUsers,
    user,
    tz,
    dummyImage,
    setIsGoogleTokenInvalidModalOpen,
    setIsMicrosoftTokenInvalidModalOpen,
    currentDate = null,
    view = 'month',
    leaveFilterUsers = null,
    useSelectedUsersForLeaves = false,
}) => {
    // Use useQueries for parallel fetching of independent queries
    // This optimizes performance by fetching multiple queries simultaneously
    // All queries now use consistent error handling via createQueryOptions
    const queryResults = useQueries([
        {
            queryKey: ["leaves", token],
            queryFn: () => fetchLeaves(token),
            ...createQueryOptions({
                enabled: !!token,
                queryName: 'Leaves',
            }),
        },
        {
            queryKey: ["events", token],
            queryFn: () => fetchEvents(token),
            ...createQueryOptions({
                enabled: !!token,
                queryName: 'Events',
            }),
        },
        {
            queryKey: ["holidays", token],
            queryFn: () => fetchHolidays(token),
            ...createQueryOptions({
                enabled: !!token,
                queryName: 'Holidays',
            }),
        },
        {
            queryKey: ['calendarUsers', token],
            queryFn: async () => {
                const response = await axios.get('/api/calendar/users', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                return response.data;
            },
            ...createQueryOptions({
                enabled: !!token,
                queryName: 'Calendar Users',
            }),
        },
    ]);

    // Extract results from useQueries
    const [
        { data: leaves, isLoading: leavesLoading, error: leavesError, refetch: refetchLeaves },
        { data: eventsData, isLoading: eventsLoading, error: eventsError, refetch: refetchEvents },
        { data: holidaysData, isLoading: holidaysLoading, error: holidaysError, refetch: refetchHolidays },
        { data: calendarUsers = [], isLoading: calendarUsersLoading },
    ] = queryResults;

    // Fetch user profiles
    const { data: userProfiles = [], isLoading: userProfileLoading } = useUserProfiles(token, {
        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 10,
    });

    // Fetch user suggestions (for birthdays and anniversaries)
    const { data: userSuggestions = [], isLoading: userSuggestionsLoading } = useUserSuggestions(token);

    // Create a stable key for selectedUsers to use in query keys
    // This ensures queries automatically refetch when selectedUsers changes
    const selectedUsersKey = useMemo(() => {
        return selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : '';
    }, [selectedUsers]);

    // Fetch Google events with proper query key including selectedUsers
    // This enables automatic refetching when selectedUsers changes
    const shouldFetchGoogleEvents = !!token && !!user?._id && !!user?.email && selectedUsers.length > 0;
    const { data: selectedGoogleEvents = [], isLoading: selectedGoogleEventsLoading, error: selectedGoogleEventsError, refetch: refetchSelectedGoogleEvents } = useQuery(
        ["selectedGoogleEvents", token, selectedUsersKey],
        () => fetchGoogleCalendarEvents(token, selectedUsers, setIsGoogleTokenInvalidModalOpen),
        createQueryOptions({
            enabled: shouldFetchGoogleEvents,
            queryName: 'Google Events',
            setModalOpen: setIsGoogleTokenInvalidModalOpen,
            onSuccess: (data) => {
                if (data.some((result) => result.events.length > 0)) {
                    setIsGoogleTokenInvalidModalOpen(false);
                }
            },
            onError: (normalizedError, originalError) => {
                // Handle Google Calendar specific errors
                if (originalError?.response?.status === 401 || originalError?.response?.status === 403) {
                    setIsGoogleTokenInvalidModalOpen(true);
                }
            },
        })
    );

    // Fetch Microsoft events with proper query key including selectedUsers
    // This enables automatic refetching when selectedUsers changes
    const shouldFetchMicrosoftEvents = !!token && !!user?._id && !!user?.email && selectedUsers.length > 0;
    const { data: selectedMicrosoftEvents = [], isLoading: selectedMicrosoftEventsLoading, error: selectedMicrosoftEventsError, refetch: refetchSelectedMicrosoftEvents } = useQuery(
        ["selectedMicrosoftEvents", token, selectedUsersKey],
        () => fetchMicrosoftCalendarEvents(token, selectedUsers, setIsMicrosoftTokenInvalidModalOpen),
        createQueryOptions({
            enabled: shouldFetchMicrosoftEvents,
            queryName: 'Microsoft Events',
            setModalOpen: setIsMicrosoftTokenInvalidModalOpen,
            onSuccess: (data) => {
                if (data.some((result) => result.events.length > 0)) {
                    setIsMicrosoftTokenInvalidModalOpen(false);
                }
            },
            onError: (normalizedError, originalError) => {
                // Handle Microsoft Calendar specific errors
                if (originalError?.response?.status === 401 || originalError?.response?.status === 403) {
                    setIsMicrosoftTokenInvalidModalOpen(true);
                }
            },
        })
    );

    // Fetch synced import events to mark already-synced calendar events
    // OLD CODE - Commented out: Using default query options
    // const { data: syncedImportEvents = { syncedEventIds: [] }, isLoading: syncedImportEventsLoading, error: syncedImportEventsError, refetch: refetchSyncedImportEvents } = useQuery(
    //     ["syncedImportEvents", token],
    //     () => fetchSyncedImportEvents(token),
    //     createQueryOptions({
    //         enabled: !!token,
    //         queryName: 'Synced Import Events',
    //     })
    // );
    // Updated: Always refetch on mount to ensure fresh data when navigating from Timer
    const { data: syncedImportEvents = { syncedEventIds: [] }, isLoading: syncedImportEventsLoading, error: syncedImportEventsError, refetch: refetchSyncedImportEvents } = useQuery(
        ["syncedImportEvents", token],
        () => fetchSyncedImportEvents(token),
        {
            ...createQueryOptions({
                enabled: !!token,
                queryName: 'Synced Import Events',
            }),
            refetchOnMount: 'always', // Always refetch on mount to ensure fresh data when navigating from Timer
            refetchOnWindowFocus: true, // Also refetch when window regains focus
        }
    );

    // Split into smaller memoized computations for better performance
    // Each event type is memoized separately, so only the changed type recomputes
    
    // Process leaves events
    // For admins/superadmins, use selectedUsers; for others, use leaveFilterUsers
    const leavesFilterUsers = useSelectedUsersForLeaves ? selectedUsers : leaveFilterUsers;
    const leavesEvents = useMemo(() => 
        processLeaves(leaves, user, tz, dummyImage, leavesFilterUsers),
        [leaves, user, tz, dummyImage, leavesFilterUsers]
    );

    // Process internal events
    const internalEvents = useMemo(() => 
        processInternalEvents(eventsData, selectedUsers, user, tz, dummyImage),
        [eventsData, selectedUsers, user, tz, dummyImage]
    );

    // Process birthday events - only generate for visible date range
    const birthdayEvents = useMemo(() => 
        processBirthdays(userSuggestions, tz, dummyImage, currentDate, view),
        [userSuggestions, tz, dummyImage, currentDate, view]
    );

    // Process anniversary events - only generate for visible date range
    const anniversaryEvents = useMemo(() => 
        processAnniversaries(userSuggestions, tz, dummyImage, currentDate, view),
        [userSuggestions, tz, dummyImage, currentDate, view]
    );

    // Process Google events
    const googleEvents = useMemo(() => 
        processGoogleEvents(selectedGoogleEvents, calendarUsers, user, tz, dummyImage),
        [selectedGoogleEvents, calendarUsers, user, tz, dummyImage]
    );

    // Process Microsoft events
    const microsoftEvents = useMemo(() => 
        processMicrosoftEvents(selectedMicrosoftEvents, calendarUsers, user, tz, dummyImage),
        [selectedMicrosoftEvents, calendarUsers, user, tz, dummyImage]
    );

    // Process holiday events
    const holidayEvents = useMemo(() => 
        processHolidays(holidaysData, user, tz),
        [holidaysData, user, tz]
    );

    // Synced event ids set for quick lookup
    const syncedEventIds = syncedImportEvents?.syncedEventIds || [];
    const syncedEventIdSet = useMemo(() => new Set((syncedEventIds || []).filter(Boolean)), [syncedEventIds]);

    // Combine all events into a unified array
    // This only recomputes when one of the individual event arrays changes
    const baseEvents = useMemo(() => [
        ...leavesEvents,
        ...internalEvents,
        ...birthdayEvents,
        ...anniversaryEvents,
        ...googleEvents,
        ...microsoftEvents,
        ...holidayEvents,
    ], [
        leavesEvents,
        internalEvents,
        birthdayEvents,
        anniversaryEvents,
        googleEvents,
        microsoftEvents,
        holidayEvents,
    ]);

    // Check if any data is loading
    const isLoading = leavesLoading || eventsLoading || userProfileLoading || userSuggestionsLoading || selectedGoogleEventsLoading || selectedMicrosoftEventsLoading || syncedImportEventsLoading;

    // Check if there are any errors
    const hasError = leavesError || eventsError || selectedGoogleEventsError || selectedMicrosoftEventsError || syncedImportEventsError;

    return {
        baseEvents,
        leaves,
        eventsData,
        holidaysData,
        userProfiles,
        calendarUsers,
        selectedGoogleEvents,
        selectedMicrosoftEvents,
        isLoading,
        hasError,
        leavesLoading,
        eventsLoading,
        userProfileLoading,
        selectedGoogleEventsLoading,
        selectedMicrosoftEventsLoading,
        leavesError,
        eventsError,
        selectedGoogleEventsError,
        selectedMicrosoftEventsError,
        syncedImportEventsError,
        refetchLeaves,
        refetchEvents,
        refetchHolidays,
        refetchSelectedGoogleEvents,
        refetchSelectedMicrosoftEvents,
        refetchSyncedImportEvents,
        syncedEventIds,
        syncedEventIdSet,
        syncedImportEventsLoading,
    };
};

